'use client';

import { cn } from '@/lib/utils';
import { useState } from 'react';

type Mode = 'coin' | 'dice' | 'picker';

export function DecisionMaker() {
  const [mode, setMode] = useState<Mode>('coin');

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex gap-2">
        {(
          [
            { id: 'coin', label: 'Coin flip' },
            { id: 'dice', label: 'Dice roll' },
            { id: 'picker', label: 'Random pick' },
          ] as const
        ).map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setMode(m.id)}
            className={cn(
              'rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
              mode === m.id
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {mode === 'coin' && <CoinFlip />}
        {mode === 'dice' && <DiceRoll />}
        {mode === 'picker' && <RandomPicker />}
      </div>
    </div>
  );
}

function CoinFlip() {
  const [result, setResult] = useState<'Heads' | 'Tails' | null>(null);
  const [flipping, setFlipping] = useState(false);

  function flip() {
    setFlipping(true);
    setTimeout(() => {
      setResult(Math.random() < 0.5 ? 'Heads' : 'Tails');
      setFlipping(false);
    }, 400);
  }

  return (
    <div className="flex flex-col items-center gap-5">
      <div
        className={cn(
          'flex size-28 items-center justify-center rounded-full border-4 border-primary bg-primary/10 text-lg font-bold text-primary transition-transform',
          flipping && 'animate-spin',
        )}
      >
        {result ?? '?'}
      </div>
      <button
        type="button"
        onClick={flip}
        className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90"
      >
        Flip
      </button>
    </div>
  );
}

function DiceRoll() {
  const [count, setCount] = useState(2);
  const [rolls, setRolls] = useState<number[] | null>(null);

  function roll() {
    setRolls(Array.from({ length: count }, () => Math.floor(Math.random() * 6) + 1));
  }

  return (
    <div className="flex flex-col items-center gap-5">
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        Dice:
        <select
          name="dice-count"
          value={count}
          onChange={(e) => setCount(Number(e.target.value))}
          className="h-9 rounded-lg border border-input bg-background px-2 text-sm text-foreground"
        >
          {[1, 2, 3, 4].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
      <div className="flex gap-3">
        {(rolls ?? Array.from({ length: count }, () => 0)).map((value, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: dice are anonymous and interchangeable — position is the only identity
            key={`die-${count}-${i}`}
            className="flex size-14 items-center justify-center rounded-xl border-2 border-primary bg-primary/10 text-xl font-bold text-primary"
          >
            {value || '?'}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={roll}
        className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90"
      >
        Roll
      </button>
    </div>
  );
}

function RandomPicker() {
  const [text, setText] = useState('Pizza\nSushi\nTacos\nBurgers');
  const [pick, setPick] = useState<string | null>(null);

  function choose() {
    const options = text
      .split('\n')
      .map((o) => o.trim())
      .filter(Boolean);
    if (options.length === 0) return;
    setPick(options[Math.floor(Math.random() * options.length)]);
  }

  return (
    <div>
      <textarea
        name="random-picker-options"
        autoComplete="off"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        placeholder="One option per line"
        className="w-full rounded-lg border border-input bg-background p-3 text-sm text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
      />
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={choose}
          className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Pick for me
        </button>
        {pick && <span className="text-lg font-bold text-primary">{pick}</span>}
      </div>
    </div>
  );
}
